import { create } from 'zustand';
import type { AdminUser, User } from '@/types/auth';
import { hasAdminPermission } from '@/utils/adminPermissions';

/** Flatten an admin's role permissions into `resource:action` strings. */
export function getAdminPermissions(admin: AdminUser): string[] {
  return admin.role?.permissions?.map((p) => `${p.resource}:${p.action}`) ?? [];
}

/**
 * Hook: whether the signed-in admin holds `permission`. Mirrors the backend
 * check, where the `super_admin` role is granted every permission even when
 * its stored list is incomplete.
 */
export function useHasAdminPermission(permission: string): boolean {
  return useAuthStore((state) => hasAdminPermission(state.admin, state.adminPermissions, permission));
}

/**
 * Authentication state for two independent sessions:
 * - the customer session (public site), and
 * - the admin session (`/admin/*`).
 *
 * Each has its own httpOnly refresh cookie on the backend, so one browser can
 * hold both at once, and signing in or out of one never changes the other.
 *
 * Security model (req 4.5):
 * - Access tokens live ONLY in memory (this store, not persisted).
 * - Refresh tokens are httpOnly cookies set by the backend and are never
 *   readable by JavaScript.
 * - Nothing auth-related is written to localStorage/sessionStorage, so a
 *   page reload relies on a silent refresh (POST /auth/refresh for customers,
 *   POST /admin/auth/refresh for admins) to restore a session.
 */
interface AuthState {
  // ── Customer session ──
  accessToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  /** True until the customer session restore has finished. */
  isBootstrapping: boolean;

  // ── Admin session ──
  adminAccessToken: string | null;
  admin: AdminUser | null;
  adminPermissions: string[];
  isAdminAuthenticated: boolean;
  /** True until the admin session restore has finished. */
  isAdminBootstrapping: boolean;

  // Customer actions
  setAccessToken: (token: string) => void;
  setUser: (user: User) => void;
  setUserAuth: (token: string, user: User) => void;
  setBootstrapping: (value: boolean) => void;
  logout: () => void;
  clearAuth: () => void;

  // Admin actions
  setAdminAuth: (token: string, admin: AdminUser) => void;
  setAdminBootstrapping: (value: boolean) => void;
  clearAdminAuth: () => void;
}

const signedOutCustomer = {
  accessToken: null,
  user: null,
  isAuthenticated: false,
};

const signedOutAdmin = {
  adminAccessToken: null,
  admin: null,
  adminPermissions: [],
  isAdminAuthenticated: false,
};

export const useAuthStore = create<AuthState>()((set) => ({
  ...signedOutCustomer,
  isBootstrapping: true,
  ...signedOutAdmin,
  isAdminBootstrapping: true,

  setAccessToken: (token) => set({ accessToken: token, isAuthenticated: true }),

  setUser: (user) => set({ user, isAuthenticated: true }),

  setUserAuth: (token, user) =>
    set({ accessToken: token, user, isAuthenticated: true }),

  setBootstrapping: (value) => set({ isBootstrapping: value }),

  logout: () => set(signedOutCustomer),

  clearAuth: () => set(signedOutCustomer),

  setAdminAuth: (token, admin) =>
    set({
      adminAccessToken: token,
      admin,
      adminPermissions: getAdminPermissions(admin),
      isAdminAuthenticated: true,
    }),

  setAdminBootstrapping: (value) => set({ isAdminBootstrapping: value }),

  clearAdminAuth: () => set(signedOutAdmin),
}));
